import {
  BadRequestError,
  ForbiddenError,
  NetworkError,
  ServerError,
  TooManyRequestsError,
  UnauthorisedError
} from '@/errors/index.js'
import i18n from '@/features/locales/i18n'
import { useAuthStore } from '@/pinia/auth'
import alertbox from '@/utils/alertbox'
import { browserTracingIntegration, captureException, init } from '@sentry/vue'
const t = i18n.global.t.bind(i18n.global)

window.addEventListener('vite:preloadError', async () => {
  await alertbox({
    title: t('common.global.newVersionTitle'),
    text: t('common.global.newVersionText')
  })
  window.location.reload()
})

export default function (app, router, config) {
  const authStore = useAuthStore()
  const allowedEnvs = ['dev', 'test', 'staging', 'production'].includes(import.meta.env.MODE)
  const isAutotest = window.location.href.includes('autotest')
  if (allowedEnvs && !isAutotest) {
    init({
      app,
      dsn: 'https://4372a73ed9634417b01faf442f11a8f7@sentry.io/2524389',
      initialScope: {
        user: { id: authStore.id, email: authStore.email }
      },
      integrations: [browserTracingIntegration({ router })],
      environment: config.environment,
      release: import.meta.env.VITE_APP_GIT_COMMIT,
      ignoreErrors: [
        'ResizeObserver loop limit exceeded',
        'ResizeObserver loop completed with undelivered notifications.',
        'Failed to fetch dynamically imported module',
        'Importing a module script failed',
        'Unable to preload CSS for'
      ],
      beforeSend(event, hint) {
        const originalException = hint?.originalException
        if (event.event_id && !window.logoutPush && originalException?.report !== false) {
          return event
        }
      }
    })
  }

  app.config.errorHandler = async function (err) {
    if (router.currentRoute.value.name === 'Error') return

    if (err instanceof BadRequestError || err instanceof ServerError) {
      router.replace({ name: 'Error', params: { eventId: err.eventId } })
    } else if (err instanceof UnauthorisedError) {
      router.replace(err.redirectURL)
    } else if (err instanceof NetworkError) {
      const { isConfirmed } = await alertbox({
        title: t('common.errors.somethingWentWrong'),
        text: t('common.errors.serverNotReached'),
        isClosable: true,
        buttons: [{ text: t('common.buttons.retry'), style: 'btn-action', action: { isConfirmed: true } }]
      })
      if (isConfirmed) {
        window.location.reload()
      }
    } else if (err instanceof TooManyRequestsError) {
      await alertbox({
        title: t('common.errors.tooManyRequests'),
        text: t('common.errors.tooManyRequestsText')
      })
    } else if (err instanceof ForbiddenError) {
      const result = await alertbox({
        title: t('common.errors.forbiddenError'),
        text: t('common.errors.forbiddenErrorText'),
        buttons: [
          { text: t('common.buttons.refresh'), style: 'btn-secondary', action: { isRefresh: true } },
          { text: t('features.login.cta'), style: 'btn-action', action: { isConfirmed: true } }
        ]
      })
      if (result.isRefresh) {
        window.location.reload()
      } else if (result.isConfirmed) {
        router.replace(err.redirectURL)
      }
    } else {
      const eventId = captureException(err)
      router.replace({
        name: 'Error',
        params: {
          eventId
        }
      })
    }
    console.error(err)
  }
}
